import { mainAPIUrl } from '../../../settings';
import { isDevelopment } from '../env/config';

const baseURL = `${mainAPIUrl}/gservices_3/ss`;

/**
 * Fetch SKU reveal data for SmartSend order
 * Endpoint: GET /gservices_3/ss/orders/:ssid/reveal_gesture
 *
 * @param {string} ssid - SmartSend ID
 * @returns {Promise<{success: boolean, data?: {title: string, imageUrl: string} | Record<string, unknown>, error?: string, code?: string, newNonce?: string}>}
 */
export const revealSku = async (ssid) => {
  const url = `${baseURL}/orders/${ssid}/reveal_gesture`;

  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    const headers = {
      Accept: 'application/json',
    };

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });

    // Check if response is ok before trying to parse JSON
    if (!response.ok) {
      // Try to parse error response, but handle cases where response isn't JSON
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (parseError) {
        // Response isn't JSON, use status text
        return {
          success: false,
          error: `Server error: ${response.status} ${response.statusText}`,
          code: 'HTTP_ERROR',
        };
      }

      return {
        success: false,
        error: errorData.message || errorData.error || 'Failed to fetch SKU reveal data',
        code: errorData.code || 'ERROR',
        data: errorData,
      };
    }

    // Extract new nonce from response headers (for subsequent POST requests that require X-Secure-Verify)
    const newNonce = response.headers.get('X-Secure-Verify');

    // Parse successful response
    const json = await response.json();

    // Success - return SKU reveal data
    // Backend returns: { code: "SUCCESS", message: "", data: { title, imageUrl } }
    // Note: Backend automatically updates isSkuRevealedByRecipient to true when this GET endpoint is called
    return {
      success: true,
      code: json.code,
      data: json.data || json,
      newNonce, // New nonce for subsequent POST requests that require X-Secure-Verify (e.g., keep as surprise)
    };
  } catch (error) {
    // Handle timeout errors
    if (error.name === 'AbortError') {
      console.error('Reveal SKU timeout:', error);
      return {
        success: false,
        error: 'Request timeout. Please try again.',
      };
    }

    // Handle network errors, CORS, etc.
    if (error.name === 'TypeError') {
      const errorMessage = error.message?.toLowerCase() || '';
      const isNetworkError =
        errorMessage.includes('fetch') ||
        errorMessage.includes('network') ||
        errorMessage.includes('failed to') ||
        errorMessage.includes('cors') ||
        errorMessage.includes('load failed');

      if (isNetworkError) {
        if (isDevelopment()) {
          console.error('Reveal SKU network error (dev details):', {
            error,
            baseURL,
            url,
          });
        } else {
          console.error('Reveal SKU network error:', error);
        }
        return {
          success: false,
          error: 'Network error: Unable to reach server. Please check your internet connection and try again.',
        };
      }
    }

    console.error('Reveal SKU error:', error);
    return {
      success: false,
      error: error.message || 'An error occurred. Please try again.',
    };
  } finally {
    // Always clear timeout
    clearTimeout(timeoutId);
  }
};
